import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  useTheme,
  MenuItem,
} from "@mui/material";
import { useShareTemplates } from "../hooks/useShareMessageTemplates";
import { ShareTemplate, TemplateFormData } from "../types/share-template.types";
import CustomTextField from "../../../../components/ui/Textfield";
import { tokens } from "../../../../theme";

interface CreateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  editTemplate?: ShareTemplate | null;
  onSave: () => void;
}

const EXAMPLE_VALUES: Record<string, string> = {
  deceasedName: "John Doe",
  deceasedFirstName: "John",
  deceasedSurname: "Doe",
  serviceDate: "Tuesday 23 December 2025",
  serviceTime: "14:30",
  serviceVenue: "Harare Convention Centre",
  serviceAddress: "123 Main Street, Harare",
  streamLink: "https://example.com/stream",
  youtubeLink: "https://youtube.com/watch?v=example",
  vimeoLink: "https://vimeo.com/example",
  bookingId: "1024",
  bookingReference: "BK-1024",
  bookingType: "Funeral",
  bookingStatus: "Confirmed",
  bookingTotal: "450.00",
  bookingCurrency: "USD",
  clientName: "Jane Doe",
  clientEmail: "jane@example.com",
  clientPhone: "+263771234567",
  deceasedDateOfDeath: "2025-12-01",
  anniversaryDate: "2025-12-01",
  companyName: "Muzukuru Funeral Services",
  funeralHomeName: "Doves Funeral Home",
  streamingCompanyName: "Muzukuru",
  averageClientReview: "4.8",
  createdDate: "2025-11-01",
  companyDescription: "Trusted funeral and memorial service provider.",
  companyPhone: "+263771234567",
  companyEmail: "info@example.com",
  companyWebsite: "https://example.com",
  companyAddress: "456 Church Street, Harare",
};

const SHARE_MESSAGE_PLACEHOLDERS = [
  "deceasedName",
  "deceasedFirstName",
  "deceasedSurname",
  "serviceDate",
  "serviceTime",
  "serviceVenue",
  "serviceAddress",
  "streamLink",
  "youtubeLink",
  "vimeoLink",
];

const BOOKING_PLACEHOLDERS = [
  "bookingId",
  "bookingReference",
  "bookingType",
  "bookingStatus",
  "bookingTotal",
  "bookingCurrency",
  "clientName",
  "clientEmail",
  "clientPhone",
  "deceasedName",
  "deceasedFirstName",
  "deceasedSurname",
  "deceasedDateOfDeath",
  "anniversaryDate",
  "companyName",
  "funeralHomeName",
  "streamingCompanyName",
  "averageClientReview",
  "createdDate",
];

const INTRODUCTION_LETTER_PLACEHOLDERS = [
  "companyName",
  "companyDescription",
  "companyPhone",
  "companyEmail",
  "companyWebsite",
  "companyAddress",
];

const ALL_PLACEHOLDERS = Object.keys(EXAMPLE_VALUES);

const PLACEHOLDERS_BY_TYPE: Record<string, string[]> = {
  share_message: SHARE_MESSAGE_PLACEHOLDERS,
  client_review: BOOKING_PLACEHOLDERS,
  thank_you_message: BOOKING_PLACEHOLDERS,
  anniversary_message: BOOKING_PLACEHOLDERS,
  introduction_letter: INTRODUCTION_LETTER_PLACEHOLDERS,
  terms_conditions: ALL_PLACEHOLDERS,
  other: ALL_PLACEHOLDERS,
};

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  open,
  onClose,
  editTemplate,
  onSave,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { saveTemplate, updateTemplate, loading } = useShareTemplates();

  const [templateData, setTemplateData] = useState<TemplateFormData>({
    name: "",
    category: "",
    template_type: "",
    body_template: "",
  });

  useEffect(() => {
    if (editTemplate) {
      setTemplateData({
        name: editTemplate.name,
        category: editTemplate.category,
        template_type: editTemplate.template_type,
        body_template: editTemplate.body_template,
      });
    } else {
      setTemplateData({
        name: "",
        category: "",
        template_type: "",
        body_template: "",
      });
    }
  }, [editTemplate]);

  const handleChange = (field: keyof ShareTemplate, value: string) => {
    setTemplateData((prev) => ({ ...prev, [field]: value }));
  };

  const availablePlaceholders = templateData.template_type
    ? (PLACEHOLDERS_BY_TYPE[templateData.template_type] ?? ALL_PLACEHOLDERS)
    : [];

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById(
      "highlighted-textarea",
    ) as HTMLTextAreaElement | null;

    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    const newValue =
      templateData.body_template.slice(0, start) +
      placeholder +
      templateData.body_template.slice(end);

    setTemplateData((prev) => ({ ...prev, body_template: newValue }));

    setTimeout(() => {
      textarea.selectionStart = textarea.selectionEnd =
        start + placeholder.length;
      textarea.focus();
    }, 0);
  };

  const highlightVariables = (text: string) => {
    let result = text;

    ALL_PLACEHOLDERS.forEach((key) => {
      const placeholder = `{${key}}`;

      result = result.replaceAll(
        placeholder,
        `<span style="color: red; font-weight: bold">${EXAMPLE_VALUES[key]}</span>`,
      );
    });

    return result;
  };

  const handleSave = async () => {
    if (editTemplate?.id) {
      await updateTemplate(editTemplate.id, templateData);
    } else {
      await saveTemplate(templateData);
    }
    onSave();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editTemplate ? "Edit Template" : "Create Template"}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" gap={3}>
          <Box flex={2} display="flex" flexDirection="column" gap={3}>
            <CustomTextField
              label="Template Name"
              fullWidth
              value={templateData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
            <CustomTextField
              label="Category"
              fullWidth
              select
              value={templateData.category}
              onChange={(e) => handleChange("category", e.target.value)}
            >
              <MenuItem value="funeral">Funeral</MenuItem>
              <MenuItem value="church">Church</MenuItem>
              <MenuItem value="conference">Conference</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </CustomTextField>

            <CustomTextField
              label="Type"
              fullWidth
              select
              value={templateData.template_type}
              onChange={(e) => handleChange("template_type", e.target.value)}
            >
              <MenuItem value="share_message">Share Message</MenuItem>
              <MenuItem value="client_review">Client Review</MenuItem>
              <MenuItem value="thank_you_message">Thank You Message</MenuItem>
              <MenuItem value="anniversary_message">
                Anniversary Message
              </MenuItem>
              <MenuItem value="terms_conditions">Terms and Conditions</MenuItem>
              <MenuItem value="introduction_letter">
                Introduction Letter
              </MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </CustomTextField>

            <CustomTextField
              id="highlighted-textarea"
              label="Message Body"
              multiline
              minRows={8}
              value={templateData.body_template}
              onChange={(e) => handleChange("body_template", e.target.value)}
            />

            <Typography variant="subtitle2" color="textSecondary">
              {templateData.template_type
                ? "These are the variables you can use in your message:"
                : "Select a Type above to see the variables available for it."}
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1}>
              {availablePlaceholders.map((p) => (
                <Chip
                  key={p}
                  label={`{${p}}`}
                  onClick={() => insertPlaceholder(`{${p}}`)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          <Box
            flex={1}
            p={2}
            border="1px solid #ddd"
            borderRadius={2}
            bgcolor={colors.primary[800]}
            height="100%"
            maxHeight="450px"
            overflow="auto"
            boxShadow="inset 0 0 4px rgba(0,0,0,0.1)"
          >
            <Typography fontWeight="bold" mb={1}>
              Live Preview
            </Typography>

            <Typography
              component="div"
              whiteSpace="pre-wrap"
              dangerouslySetInnerHTML={{
                __html: highlightVariables(templateData.body_template),
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={loading} onClick={handleSave} variant="contained">
          {editTemplate ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateModal;
